import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';
import React from 'react';
import useAgentic from '../../hooks/useAgentic';

const STATUS_COLOR = {
  started: 'default',
  running: 'warning',
  completed: 'success',
  failed: 'error',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return dateString;
  }
};

function Sessions() {
  const {
    sessions,
    sessionsPagination,
    selectedSessionId,
    setSelectedSessionId,
    sessionsLoading,
    loadSessions,
    loadSessionDetails,
    timelinePagination,
  } = useAgentic();

  const handleSelectSession = async (sessionId) => {
    setSelectedSessionId(sessionId);
    await loadSessionDetails(sessionId, 1, timelinePagination.limit || 200);
  };

  const handleSessionsPage = async (nextPage) => {
    if (nextPage < 1 || nextPage > (sessionsPagination.totalPages || 1)) return;
    await loadSessions(nextPage, sessionsPagination.limit || 20);
  };

  return (
    <Box sx={{ flex: { xs: '1 1 auto', lg: '0 0 35%' }, minWidth: 0 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Sessions</Typography>
            <Button
              size="small"
              onClick={() => loadSessions(sessionsPagination.page || 1, sessionsPagination.limit || 20)}
            >
              Refresh
            </Button>
          </Stack>

          {sessionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No sessions found.
            </Typography>
          ) : (
            <>
              <List disablePadding>
                {sessions.map((session) => (
                  <ListItemButton
                    key={session._id}
                    selected={selectedSessionId === session._id}
                    onClick={() => handleSelectSession(session._id)}
                    sx={{ px: 0 }}
                  >
                    <ListItemText
                      primary={session.inputText || 'Untitled prompt'}
                      secondary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                          <Chip
                            size="small"
                            label={session.status || 'unknown'}
                            color={STATUS_COLOR[session.status] || 'default'}
                            variant="outlined"
                          />
                          <Typography component="span" variant="caption" color="text.secondary">
                            {formatDate(session.createdAt)}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={(sessionsPagination.page || 1) <= 1 || sessionsLoading}
                  onClick={() => handleSessionsPage((sessionsPagination.page || 1) - 1)}
                >
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {sessionsPagination.page || 1} of {sessionsPagination.totalPages || 1}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    (sessionsPagination.page || 1) >= (sessionsPagination.totalPages || 1) ||
                    sessionsLoading
                  }
                  onClick={() => handleSessionsPage((sessionsPagination.page || 1) + 1)}
                >
                  Next
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Sessions;
